/**
 * ViewPlanningV2.utils.js
 *
 * Helpers behind the View Planning page — response mapping and table locale
 * config kept out of the component tree so the page and drill-down
 * components stay presentational.
 */
import React from "react";
import { Typography } from "antd";

const { Text } = Typography;

// While loading, antd still renders emptyText under its spin overlay — swap
// in a tall spacer so the spinner has room and the empty message never
// flashes before the data arrives.
export const tableEmptyLocale = (rows, emptyMessage) => ({
  emptyText:
    rows === null ? <div style={{ height: 120 }} /> : <Text type="secondary">{emptyMessage}</Text>,
});

// GET /talent-mobility/jd-active-employees rows → occupant rows ranked by
// SMI (highest first). Display id is the HRIS emp_id, never the unique id —
// userId stays on `id` for availability matching and navigation.
export const mapJdActiveEmployeesToOccupants = (employees) =>
  employees
    .map((employee) => ({
      // null (never coerced to 0) when this occupant hasn't updated their
      // profile against this role yet — "not scored" vs. "scored zero"
      // (see useTalentMobilityData's equivalent apiOccupants mapping).
      // buildScoreColumn's shared SMI/SRI render falls back to a dash +
      // tooltip for a null value.
      matchPct: employee.profile_score == null ? null : Math.round(employee.profile_score),
      person: {
        id: employee.userId,
        employeeId: employee.emp_id,
        name: employee.name,
        nextRoles: null,
      },
    }))
    .sort((a, b) => b.matchPct - a.matchPct);

// The saved plan's availability — who was marked available…
export const getAvailableEmployeeIds = (planEmployees) =>
  new Set((planEmployees || []).map((employee) => employee.employee_unique_id));

// …and which roles each of them was targeted to.
export const buildTargetedRoleIdsByEmployee = (planEmployees) => {
  const byEmployee = new Map();
  (planEmployees || []).forEach((employee) => {
    byEmployee.set(
      employee.employee_unique_id,
      new Set((employee.targeted_roles || []).map((role) => role.jd_sb_unique_id))
    );
  });
  return byEmployee;
};

// …and the Reason for Shortlisting each one was saved with — only
// employees that actually carry a non-blank reason get an entry, so the
// column can render its icon purely on presence.
export const buildShortlistReasonByEmployee = (planEmployees) =>
  new Map(
    (planEmployees || [])
      .filter((employee) => employee.shortlist_reason?.trim())
      .map((employee) => [employee.employee_unique_id, employee.shortlist_reason.trim()])
  );

// The Retained/At Risk cutoff — the plan row's stored target_position when
// it's a positive number, otherwise everyone counts as retained.
export const resolveCutoff = (storedTarget, occupantCount) => {
  const target = Number(storedTarget);
  return Number.isFinite(target) && target > 0 ? target : occupantCount;
};
